import React, { useEffect } from 'react';
import {
  Box,
  Button,
  GridContainer,
  ModalDialog,
  ModalDialogBody,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogTitle,
  Row,
  Typography,
} from '@vp/swan';
import { Validation } from '@vp/upload-and-print-validator';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { usePriceContext } from 'src/hooks/PriceContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { useUploadContext } from 'src/hooks/UploadContextHooks';
import { PricingContext, usePricingContext } from 'src/hooks/usePricingContext';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import optionsChangeArrow from 'src/images/validation/optionsChangeArrow.svg';
import { ProductInformation } from 'src/providers/ProductContextProvider';
import { TrackingConfiguration } from 'src/providers/TrackingContextProvider';
import { processAndValidateUpload, Upload } from 'src/services/UploadAndPrintService';
import { text } from 'src/utils/localizationUtil';
import {
  filterRecommendedProductOptionsToJustProductPageOptions,
  updateProductOptionQueryParameters,
} from 'src/utils/productOptionUtils';
import { createTrackingDataForProduct, TrackingDataForProduct } from 'src/utils/trackingUtil';
import { ProductOptionSelectionDisplay } from './ProductOptionSelectionDisplay';
import { AnalyticsEventData, sendEventTracking } from '../Analytics';

export interface ProductOptionChangesPromptProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isMobile: boolean;
}

//An upload is split apart here, because we need to call this from within a useEffect,
//and passing in the full upload requires the useEffect to have a dependency on Upload, which
//will change to a new object reference as this function fires (and then re-trigger the useEffect
//infinitely).  This way the useEffect can have a dependency on the strings that make up an upload,
//and use string equality comparison to determine if the upload changes, rather than reference equality comparison
//to an object
export async function sendToPrepress(
  uploadFileUri: string | undefined,
  uploadFileName: string | undefined,
  uploadThumbnailUri: string | undefined,
  setUpload: (newUploads: Upload | null) => void,
  setValidations: (newValidations: Validation[] | null) => void,
  setIsValidating: (isValidating: boolean) => void,
  productInformation: ProductInformation,
  trackingConfiguration: TrackingConfiguration | null,
  trackingDataForProduct: TrackingDataForProduct,
  isVat: boolean
): Promise<void> {
  if (!uploadFileUri) {
    throw Error('Upload is null or undefined');
  }

  setIsValidating(true);

  const [processedUploadUri, processUploadResponse] = await processAndValidateUpload(
    uploadFileUri,
    productInformation,
    trackingConfiguration,
    trackingDataForProduct,
    isVat
  );

  const newUpload: Upload = {
    fileUri: uploadFileUri,
    uploadedFileName: uploadFileName,
    thumbnailUrl: uploadThumbnailUri,
    processedUploadUri: processedUploadUri,
    renderingInstructionsUrl: processUploadResponse.renderingInstructionsUrl,
  };

  setUpload(newUpload);

  setValidations(processUploadResponse.validations);
  setIsValidating(false);
}

async function saveChanges(
  upload: Upload | null,
  productInformation: ProductInformation,
  setValidations: (newValidations: Validation[] | null) => void,
  setIsValidating: (isValidating: boolean) => void,
  setProductInformation: (newInfo: ProductInformation) => void,
  setIsOpen: (isOpen: boolean) => void,
  setError: (error: unknown) => void,
  setUpload: (upload: Upload | null) => void,
  trackingConfiguration: TrackingConfiguration | null,
  isVat: boolean
) {
  try {
    const productOptionsToAccept = await filterRecommendedProductOptionsToJustProductPageOptions(
      productInformation.productKey,
      productInformation.productVersion,
      productInformation?.recommendedProductData?.productOptions ?? {}
    );

    if (productOptionsToAccept) {
      const newProductInformation = {
        ...productInformation,
        recommendedProductData: undefined,
        selectedOptions: productOptionsToAccept,
      };
      setIsOpen(false);
      setValidations(null);
      updateProductOptionQueryParameters(productOptionsToAccept);

      setProductInformation(newProductInformation);
      const trackingDataForProduct = createTrackingDataForProduct(newProductInformation);

      sendEventTracking({
        ...trackingConfiguration,
        ...trackingDataForProduct,
        action: 'Fly-Out Clicked',
        label: `${newProductInformation.productName} Fly-out`,
        category: 'Fly-Out',
        eventDetail: 'Save Changes',
        variant: newProductInformation.selectedOptions,
      } as AnalyticsEventData);

      await sendToPrepress(
        upload?.fileUri,
        upload?.uploadedFileName,
        upload?.thumbnailUrl,
        setUpload,
        setValidations,
        setIsValidating,
        {
          ...newProductInformation,
        },
        trackingConfiguration,
        trackingDataForProduct,
        isVat
      );
    } else {
      throw Error(`Tried to save null/undefined recommended product option changes`);
    }
  } catch (e) {
    console.error('Error updating product options', e);
    setError(e);
    setUpload(null);
  } finally {
    setIsOpen(false);
    setIsValidating(false);
  }
}

export const ProductOptionChangesPrompt = ({
  isOpen,
  setIsOpen,
  isMobile,
}: ProductOptionChangesPromptProps): JSX.Element => {
  const { trackingConfiguration } = useTrackingContext();
  const { productInformation, setProductInformation, completedProductInformation } = useProductContext();
  const { setValidations, setIsValidating } = useValidationContext();
  const { upload, setUpload } = useUploadContext();
  const { setError } = useErrorContext();
  const { price } = usePriceContext();
  const pricingContext = usePricingContext() || ({} as PricingContext);

  const closeModal = () => {
    sendEventTracking({
      ...trackingConfiguration,
      ...createTrackingDataForProduct(productInformation),
      action: 'Fly-Out Closed',
      label: `${productInformation.productName} Fly-out`,
      category: 'Fly-Out',
      variant: productInformation.selectedOptions,
    } as AnalyticsEventData);

    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen && productInformation.recommendedProductData && upload?.fileUri) {
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct({
          mpvId: productInformation.mpvId,
          productKey: productInformation.productKey,
          productName: productInformation.productName,
          productVersion: productInformation.productVersion,
        }),
        action: 'Fly-Out Viewed',
        label: `${productInformation.productName} Fly-out`,
        category: 'Fly-Out',
        variant: productInformation.selectedOptions,
      } as AnalyticsEventData);
    }
  }, [
    isOpen,
    productInformation.recommendedProductData,
    productInformation.mpvId,
    productInformation.productName,
    productInformation.productKey,
    productInformation.productVersion,
    productInformation.selectedOptions,
    upload,
    trackingConfiguration,
  ]);

  if (!productInformation.recommendedProductData || !upload?.fileUri) {
    return <></>;
  }

  const optionsWithRecommendations: string[] = [];

  Object.entries(productInformation.recommendedProductData.productOptions).forEach(([key, value]) => {
    if (productInformation.selectedOptions[key] !== value) {
      optionsWithRecommendations.push(key);
    }
  });

  return (
    <ModalDialog isOpen={isOpen} onRequestDismiss={closeModal} padding={'7'}>
      <ModalDialogContent aria-labelledby="dialogTitle">
        <ModalDialogCloseButton visuallyHiddenLabel="Close" />
        <Box style={{ textAlign: 'center' }}>
          <ModalDialogTitle marginBottom={'4'} id="dialogTitle" fontWeight="bold">
            {text('product-option-suggestion.modal-title')}
          </ModalDialogTitle>

          <ModalDialogBody marginBottom={'4'}>
            <Typography marginBottom={'7'}>{text('product-option-suggestion.modal-body')}</Typography>
            <GridContainer>
              <Row>
                <ProductOptionSelectionDisplay
                  selections={completedProductInformation?.selectedOptions ?? productInformation.selectedOptions}
                  optionsToDisplay={optionsWithRecommendations}
                  isOriginal={true}
                  isMobile={isMobile}
                  price={price}
                />
                <Box style={{ width: '10%', paddingTop: '35' }}>
                  <img src={optionsChangeArrow} alt="" />
                </Box>
                <ProductOptionSelectionDisplay
                  selections={productInformation.recommendedProductData.productOptions}
                  optionsToDisplay={optionsWithRecommendations}
                  isOriginal={false}
                  isMobile={isMobile}
                  price={productInformation.recommendedProductData.price}
                />
              </Row>
            </GridContainer>
          </ModalDialogBody>

          <Box marginTop={'8'}>
            <Button skin="secondary" onClick={closeModal} marginX={'2'} size="mini">
              {text('product-option-suggestion.cancel')}
            </Button>
            <Button
              skin="primary"
              onClick={() =>
                saveChanges(
                  upload,
                  productInformation,
                  setValidations,
                  setIsValidating,
                  setProductInformation,
                  setIsOpen,
                  setError,
                  setUpload,
                  trackingConfiguration,
                  pricingContext.vatInclusive
                )
              }
              marginX={'2'}
              size="mini"
            >
              {text('product-option-suggestion.save-changes')}
            </Button>
          </Box>
        </Box>
      </ModalDialogContent>
    </ModalDialog>
  );
};
